import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../api/apiService';
import type { Training } from '../../types';
import { Button } from '../../components/ui/Button';
import { Drawer } from '../../components/ui/Drawer';
import { Modal } from '../../components/ui/Modal';
import { TrainingDetails } from '../../components/TrainingDetails';
import { Plus, Eye, Edit2, Trash2 } from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';
import { useToast } from '../../contexts/ToastContext';
import './CoachTrainings.css';

export const CoachTrainings: React.FC = () => {
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTraining, setSelectedTraining] = useState<Training | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [trainingToDelete, setTrainingToDelete] = useState<Training | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();

  useEffect(() => {
    loadTrainings();
  }, []);

  const loadTrainings = async () => {
    try {
      const data = await apiService.getCoachTrainings();
      setTrainings(data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    } catch (error) {
      console.error('Error loading trainings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getExerciseCount = (training: Training): number => {
    let count = 0;

    if (training.warmup_section?.exercises) {
      count += training.warmup_section.exercises.length;
    }

    if (training.main_section?.exercises) {
      count += training.main_section.exercises.length;
    }

    if (training.cooldown_section?.exercises) {
      count += training.cooldown_section.exercises.length;
    }

    if (count === 0 && training.exercises) {
      count = training.exercises.length;
    }

    return count;
  };

  const calculateEstimatedTime = (training: Training): number => {
    let totalSeconds = 0;

    // Warmup section
    if (training.warmup_section?.exercises && training.warmup_section.exercises.length > 0) {
      const count = training.warmup_section.exercises.length;
      const seconds = training.warmup_section.params?.seconds || 0;
      const rest = training.warmup_section.params?.rest_between_exercises || 0;

      totalSeconds += count * seconds;
      totalSeconds += (count - 1) * rest;
    }

    // Main section
    if (training.main_section?.exercises && training.main_section.exercises.length > 0) {
      const count = training.main_section.exercises.length;
      const series = training.main_section.params?.series || 1;
      const seconds = training.main_section.params?.seconds || 0;
      const restBetweenSeries = training.main_section.params?.rest_between_series || 0;
      const restBetweenExercises = training.main_section.params?.rest_between_exercises || 0;

      totalSeconds += count * series * seconds;
      totalSeconds += count * (series - 1) * restBetweenSeries;
      totalSeconds += (count - 1) * restBetweenExercises;
    }

    // Cooldown section
    if (training.cooldown_section?.exercises && training.cooldown_section.exercises.length > 0) {
      const count = training.cooldown_section.exercises.length;
      const seconds = training.cooldown_section.params?.seconds || 0;
      const rest = training.cooldown_section.params?.rest_between_exercises || 0;

      totalSeconds += count * seconds;
      totalSeconds += (count - 1) * rest;
    }

    return totalSeconds;
  };

  const formatTime = (totalSeconds: number): string => {
    if (totalSeconds === 0) return '0s';

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes > 0 && seconds > 0) {
      return `${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  };

  const handleViewTraining = async (training: Training) => {
    try {
      // Load full training details
      const fullTraining = await apiService.getTraining(training.id);
      setSelectedTraining(fullTraining);
      setIsDrawerOpen(true);
    } catch (error) {
      console.error('Error loading training details:', error);
    }
  };

  const handleEditTraining = (training: Training) => {
    navigate(`/coach/trainings/edit/${training.id}`);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setTimeout(() => setSelectedTraining(null), 300);
  };

  const handleOpenDeleteModal = (training: Training) => {
    setTrainingToDelete(training);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setTimeout(() => setTrainingToDelete(null), 300);
  };

  const handleDeleteTraining = async () => {
    if (!trainingToDelete) return;

    setIsDeleting(true);
    try {
      await apiService.deleteTraining(trainingToDelete.id);
      showToast(t('trainingDeleted'), 'success');
      handleCloseDeleteModal();
      loadTrainings();
    } catch (error) {
      console.error('Error deleting training:', error);
      showToast(t('error'), 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <div className="text-center p-4">{t('loading')}</div>;
  }

  return (
    <div className="coach-trainings-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('trainings')}</h1>
          <p className="page-subtitle">{t('manageYourTrainings')}</p>
        </div>
        <Button onClick={() => navigate('/coach/trainings/create')}>
          <Plus size={20} />
          {t('createTraining')}
        </Button>
      </div>

      {trainings.length === 0 ? (
        <div className="empty-state">
          <p>{t('noTrainingsFound')}</p>
          <Button onClick={() => navigate('/coach/trainings/create')}>
            <Plus size={20} />
            {t('createFirstTraining')}
          </Button>
        </div>
      ) : (
        <div className="trainings-list">
          {trainings.map((training) => {
            const exerciseCount = getExerciseCount(training);
            const estimatedTime = calculateEstimatedTime(training);
            return (
              <div key={training.id} className="training-card">
                {/* Card Header */}
                <div className="training-card-header">
                  <div className="training-card-title-section">
                    <h3 className="training-card-title">{training.title}</h3>
                    <span className="training-card-date">{formatDate(training.date, true)}</span>
                  </div>
                  <div className="training-card-actions">
                    <button
                      className="action-btn view-btn"
                      onClick={() => handleViewTraining(training)}
                      title={t('view')}
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="action-btn edit-btn"
                      onClick={() => handleEditTraining(training)}
                      title={t('edit')}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={() => handleOpenDeleteModal(training)}
                      title={t('delete')}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Card Meta */}
                <div className="training-card-meta">
                  <span className="training-card-student">
                    {training.user?.name || '-'}
                  </span>
                  <span className="training-card-count">
                    {exerciseCount} {t('exercises')}
                  </span>
                  {estimatedTime > 0 && (
                    <span className="training-card-time">
                      {formatTime(estimatedTime)}
                    </span>
                  )}
                </div>

                {/* Exercise Sections in Columns */}
                <div className="training-exercises-columns">
                  {training.warmup_section && training.warmup_section.exercises.length > 0 && (
                    <div className="exercise-column">
                      <h4 className="exercise-column-title">{t('warmup')}</h4>
                      <ul className="exercise-column-list">
                        {training.warmup_section.exercises.map((exercise, idx) => (
                          <li key={idx}>{exercise.name}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {training.main_section && training.main_section.exercises.length > 0 && (
                    <div className="exercise-column">
                      <h4 className="exercise-column-title">{t('mainPart')}</h4>
                      <ul className="exercise-column-list">
                        {training.main_section.exercises.map((exercise, idx) => (
                          <li key={idx}>{exercise.name}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {training.cooldown_section && training.cooldown_section.exercises.length > 0 && (
                    <div className="exercise-column">
                      <h4 className="exercise-column-title">{t('cooldown')}</h4>
                      <ul className="exercise-column-list">
                        {training.cooldown_section.exercises.map((exercise, idx) => (
                          <li key={idx}>{exercise.name}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {training.exercises && training.exercises.length > 0 && !training.warmup_section && !training.main_section && !training.cooldown_section && (
                    <div className="exercise-column">
                      <h4 className="exercise-column-title">{t('exercises')}</h4>
                      <ul className="exercise-column-list">
                        {training.exercises.map((exercise, idx) => (
                          <li key={idx}>{exercise.exercise?.name || `Exercise ${idx + 1}`}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Observations */}
                {training.observations && (
                  <div className="training-card-observations">
                    <strong>{t('observations')}:</strong> {training.observations}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        title={t('trainingDetails')}
        width="700px"
      >
        {selectedTraining && <TrainingDetails training={selectedTraining} />}
      </Drawer>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        title={t('delete')}
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={isDeleting}>
              {t('cancel')}
            </Button>
            <Button variant="primary" onClick={handleDeleteTraining} disabled={isDeleting}>
              {isDeleting ? t('loading') : t('delete')}
            </Button>
          </>
        }
      >
        <p>{t('confirmDelete')}</p>
        {trainingToDelete && (
          <div style={{ marginTop: '1rem' }}>
            <strong>{trainingToDelete.title}</strong>
            <br />
            <small>{formatDate(trainingToDelete.date, true)}</small>
          </div>
        )}
      </Modal>
    </div>
  );
};
